import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import Styles from './styled';
import Dropdown from '../Inputs/Dropdown';
import Input from '../Input';
import Button from '../Button';
import Radio from '../Inputs/Radio';
import states from '../../../data/states';
import submitContactForm from '../../../services/submitContactForm';
import MessageSuccess from '../Icons/MessageSuccess';
import { useFormik } from 'formik';
import useGTM from '../../../hooks/useGTM';
import createGTMClass from '../../../util/createGTMClass';

const statesShort = states.reduce(
  (_states, { value }) => [..._states, value],
  []
);

const initialValues = {
  reason: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  bestTime: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  comments: '',
};

const validationSchema = {
  reason: Yup.string(),
  firstName: Yup.string()
    .max(40, 'First name is too long.')
    .required('First name required.'),
  lastName: Yup.string()
    .max(50, 'Last name is too long.')
    .required('Last name required.'),
  email: Yup.string()
    .email('You must enter a valid email.')
    .required('Email required.'),
  phone: Yup.string()
    .matches(
      /^(\+?( |-|\.)?\d{1,2}( |-|\.)?)?(\(?\d{3}\)?|\d{3})( |-|\.)?(\d{3}( |-|\.)?\d{4})+$/,
      'Phone must be formatted correctly ###-###-####.'
    )
    .required('Phone number required.'),
  bestTime: Yup.string().required('Best time to reach you must be selected.'),
  address: Yup.string().max(80, 'Address is too long.'),
  city: Yup.string().max(50, 'City is too long.'),
  state: Yup.string()
    .matches(
      new RegExp(statesShort.join('|')),
      'State must be selected from the list.'
    )
    .required('State required.'),
  zip: Yup.string().matches(
    /^\d{5}(-?\d{4})?$/,
    'Zip must be formatted either XXXXX or XXXXX-XXXX.'
  ),
  comments: Yup.string().max(3000, 'Comments are too long.'),
};

const reasons = [
  {
    label: 'A loan for a new home',
    value: 'new',
  },
  {
    label: 'A loan for a pre-owned or repossessed home',
    value: 'preOwned',
  },
  {
    label: 'A question regarding my current loan',
    value: 'question',
  },
];

const bestTime = [
  {
    label: 'Morning',
    value: 'morning',
  },
  {
    label: 'Afternoon',
    value: 'afternoon',
  },
  {
    label: 'Evening',
    value: 'evening',
  },
];

const ContactUsForm = () => {
  const [triggerGTMEvent] = useGTM();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const onSubmit = (_values, { setSubmitting, setStatus, resetForm }) => {
    setSubmitting(true);
    const values = Object.keys(_values).reduce(
      (vals, key) => ({
        ...vals,
        [key]: _values[key] || null,
      }),
      {}
    );

    submitContactForm(values)
      .then(data => {
        setIsSubmitted(true);
        setSubmitting(false);
        resetForm(initialValues);
      })
      .then(
        triggerGTMEvent({
          name: 'vmf.cta_click',
          location: 'Contact Open',
          category: 'contact_us',
          action: 'form',
          label: 'contact_submit',
        })
      )
      .catch(error => {
        setIsSubmitted(false);
        setSubmitting(false);
        setStatus(error.response.data);
      })
      .finally(() => {
        window.scrollTo(0, 200);
      });
  };

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    errors,
    touched,
    values,
    status,
    isSubmitting,
  } = useFormik({
    initialValues,
    validationSchema: Yup.object().shape(validationSchema),
    onSubmit,
  });

  const handleSubmitClick = e => {
    e.stopPropagation();
    e.preventDefault();

    handleSubmit();
  };

  if (isSubmitted) {
    return (
      <Styles>
        <section className='thank-you'>
          <h2>Thank you!</h2>
          <div className='content'>
            <MessageSuccess />
            <p>
              Your request has been submitted and we’ll have a response back to
              you within 24-48 hours (or sooner) by email. Be on the lookout for
              a reply to the email address we have on file for you. If you don’t
              receive your email reply, please check your Spam/Junk mailbox just
              in case it was routed there. We’re always happy to hear from you!
            </p>
            <Button onClick={() => setIsSubmitted(false)}>Okay</Button>
          </div>
        </section>
      </Styles>
    );
  }

  return (
    <Styles>
      <form>
        <fieldset disabled={isSubmitting}>
          <legend className='type-h3'>Send us a message</legend>
          <div className='row'>
            <Dropdown
              label='I’m interested in speaking with someone about:'
              values={reasons}
              note='Required'
              name='reason'
              onChange={handleChange}
              onBlur={handleBlur}
              value={values?.reason}
              error={errors?.reason}
              touched={touched?.reason}
              canSearch={false}
            />
          </div>
          {values?.reason && (
            <>
              {values.reason == 'question' && (
                <div className='row'>
                  <Input
                    type='text'
                    formatter='number'
                    name='loanNumber'
                    label='Loan Number'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values?.loanNumber}
                    error={errors?.loanNumber}
                    touched={touched?.loanNumber}
                  />
                </div>
              )}
              <div className='row'>
                <Input
                  type='text'
                  name='firstName'
                  label='First name'
                  note='Required'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values?.firstName}
                  error={errors?.firstName}
                  touched={touched?.firstName}
                />
                <Input
                  type='text'
                  name='lastName'
                  label='Last name'
                  note='Required'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values?.lastName}
                  error={errors?.lastName}
                  touched={touched?.lastName}
                />
              </div>
              <div className='row'>
                <Input
                  type='text'
                  name='email'
                  label='Email address'
                  note='Required'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values?.email}
                  error={errors?.email}
                  touched={touched?.email}
                />
                <Input
                  type='text'
                  name='phone'
                  label='Phone number'
                  note='Required'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values?.phone}
                  error={errors?.phone}
                  touched={touched?.phone}
                  formatter='phone'
                />
              </div>
              <div className='row'>
                <Radio
                  name='bestTime'
                  label='Best time to reach you:'
                  options={bestTime}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values?.bestTime}
                  error={errors?.bestTime}
                  touched={touched?.bestTime}
                />
              </div>
              <div className='row'>
                <Input
                  type='text'
                  name='address'
                  label='Address'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values?.address}
                  error={errors?.address}
                  touched={touched?.address}
                />
              </div>
              <div className='row'>
                <Input
                  type='text'
                  name='city'
                  label='City'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values?.city}
                  error={errors?.city}
                  touched={touched?.city}
                />
                <div className='row two-column'>
                  <Dropdown
                    type='text'
                    name='state'
                    label='State'
                    className='mobile-half'
                    note='Required'
                    values={states}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values?.state}
                    error={errors?.state}
                    touched={touched?.state}
                    showFormatted={false}
                  />
                  <Input
                    type='text'
                    name='zip'
                    label='Zip'
                    className='mobile-half'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values?.zip}
                    error={errors?.zip}
                    touched={touched?.zip}
                  />
                </div>
              </div>
              <div className='row'>
                <Input
                  type='textarea'
                  label='Comments'
                  name='comments'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values?.comments}
                  error={errors?.comments}
                  touched={touched?.comments}
                />
              </div>
              <div className='row'>
                <Button
                  appearance='dark'
                  type='submit'
                  size='large'
                  onClick={handleSubmitClick}
                  className={createGTMClass('form', 'form', 'button')}
                >
                  Submit
                </Button>
              </div>
            </>
          )}
        </fieldset>
      </form>
    </Styles>
  );
};

ContactUsForm.propTypes = {};

export default ContactUsForm;
